import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionDetails, Box, Button, TextField, Typography } from '@mui/material';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useMutation, useQuery } from '@apollo/client';
import { liveInternalOrderDetailsContext } from 'apollo/order-live-updates';
import { linkInternalOrderDetails } from 'apollo/internal-order-details-cache';
import { useDraftState } from 'hooks/useDraftState';
import { getInternalOrderNotesTemplate, notesTemplate } from 'apollo/templates/order';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { Order } from 'models/order';
import { useRefreshOrder } from 'hooks/useRefreshOrder';

const EXPANDED_STORAGE_KEY = 'stickyInternalNotesExpanded';

const AccordionSummary = styled((props: AccordionSummaryProps) => <MuiAccordionSummary {...props} />)(() => ({
    minHeight: 36,
    '&.Mui-expanded': {
        minHeight: 36,
    },
    '& .MuiAccordionSummary-content': {
        margin: '6px 0',
        '&.Mui-expanded': {
            margin: '6px 0',
        },
    },
}));

type StickyInternalNotesProps = {
    order: Order;
};

const StickyInternalNotes: FC<StickyInternalNotesProps> = ({ order }: StickyInternalNotesProps) => {
    const { t: translate } = useTranslation(['order-event']);
    const currentOrganization = currentOrgVar();
    const refreshOrder = useRefreshOrder();
    const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(() => localStorage.getItem(EXPANDED_STORAGE_KEY) === 'true');
    const { showBoundary } = useDenthubErrorBoundary();

    // Shares the cached InternalOrderDetails with the internal tab and is a live view
    // (DD-3231); the textarea edits a draft, so a colleague's save never wipes typing.
    const { loading: notesLoading, data } = useQuery(getInternalOrderNotesTemplate, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        context: liveInternalOrderDetailsContext(order._id),
        variables: {
            orderId: order._id,
        },
        onError(error) {
            showBoundary(error);
        },
    });
    const savedNotes: string = data?.internalOrderDetails?.notes ?? '';
    const notes = useDraftState<string>(savedNotes);

    const [saveNotes, { loading: notesSaving }] = useMutation(notesTemplate, {
        update(cache, { data }) {
            linkInternalOrderDetails(cache, data?.notes);
        },
        onCompleted: () => {
            setShowSuccessSnackbar(true);
            // isInternalOrderData is derived server-side: re-read the order (DD-3231).
            refreshOrder(order._id);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleSaveNotesClick = async () => {
        await saveNotes({
            variables: {
                labId: currentOrganization._id,
                orderId: order._id,
                notes: notes.value,
            },
        });
    };

    const onNotesChangeEvent = (event: ChangeEvent<HTMLTextAreaElement>) => {
        notes.setValue(event.target.value);
        setShowSuccessSnackbar(false);
    };

    const handleToggleExpand = () => {
        setIsExpanded((previousExpanded) => {
            localStorage.setItem(EXPANDED_STORAGE_KEY, String(!previousExpanded));
            return !previousExpanded;
        });
    };

    if (notesLoading) {
        return null;
    }

    const hasInternalNotes = Boolean(savedNotes);

    return (
        <Fragment>
            {showSuccessSnackbar ? (
                <AlertSnackbar type="success" message={translate('order-event:SUCCESS_NOTES')} />
            ) : undefined}
            {/* Width matches the content column of the Grid above: 75% (md=9) minus its share of the 16px gutter */}
            <Box
                mt={2}
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1000,
                    boxShadow: 4,
                    width: { xs: '100%', md: 'calc(75% - 4px)' },
                }}
            >
                <Accordion expanded={isExpanded} onChange={handleToggleExpand} disableGutters>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={
                            hasInternalNotes
                                ? { background: '#e3f2fd', borderTop: '3px #1976d2 solid' }
                                : { background: '#f1f1f1', borderTop: '1px #bbb solid' }
                        }
                        data-test="main-order-view.sticky-internal-notes-accordion"
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <DescriptionOutlinedIcon
                                fontSize="small"
                                sx={{ color: hasInternalNotes ? 'primary.main' : 'text.secondary' }}
                            />
                            <Typography variant="body2" fontWeight={500}>
                                <Box component="span" fontWeight={700}>
                                    {translate('order-event:INTERNAL_NOTES')}:
                                </Box>
                                &nbsp;
                                {hasInternalNotes
                                    ? translate('order-event:HAS_INTERNAL_NOTES')
                                    : translate('order-event:NO_INTERNAL_NOTES')}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TextField
                            value={notes.value}
                            onChange={onNotesChangeEvent}
                            fullWidth
                            multiline
                            rows={12}
                            inputProps={{ 'aria-label': translate('order-event:INTERNAL_NOTES') }}
                            data-test="main-order-view.sticky-internal-notes-field"
                        />
                        {!order.isImportedOrder && (
                            <Box mt={1}>
                                <Button
                                    variant="contained"
                                    disabled={!notes.value || notesSaving || !notes.isDirty}
                                    onClick={handleSaveNotesClick}
                                    data-test="main-order-view.sticky-internal-notes-save-btn"
                                >
                                    {translate('order-event:SAVE_NOTES')}
                                </Button>
                            </Box>
                        )}
                    </AccordionDetails>
                </Accordion>
            </Box>
        </Fragment>
    );
};

export default StickyInternalNotes;
